import React, { useState, useCallback, useMemo, useEffect } from 'react';
import confetti from 'canvas-confetti';

export interface PrizeSegment {
  label: string;
  weight: number;
}

interface PrizeWheelProps {
  segments: PrizeSegment[];
  onSpinComplete?: (winningLabel: string) => void;
  disabled?: boolean;
  confettiOnWin?: boolean;
  compact?: boolean;
  /** When true (default), hide the spin control after the first completed spin. */
  singleUse?: boolean;
}

/** Matches wheel fills — use in admin legend */
export const PRIZE_WHEEL_SEGMENT_COLORS = [
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#059669',
  '#db2777',
] as const;

const pickWeightedIndex = (segments: PrizeSegment[]): number => {
  const total = segments.reduce((s, x) => s + Math.max(0.001, x.weight), 0);
  let r = Math.random() * total;
  for (let i = 0; i < segments.length; i++) {
    r -= Math.max(0.001, segments[i].weight);
    if (r <= 0) return i;
  }
  return segments.length - 1;
};

const normDeg = (deg: number) => ((deg % 360) + 360) % 360;

/** Degrees from top, clockwise → Cartesian (SVG y down), center origin */
function pointOnCircle(R: number, degFromTopCW: number) {
  const rad = (degFromTopCW * Math.PI) / 180;
  return { x: R * Math.sin(rad), y: -R * Math.cos(rad) };
}

/** Wedge from degStart to degEnd (clockwise, degrees), center 0,0 */
function wedgePath(R: number, degStart: number, degEnd: number) {
  const p1 = pointOnCircle(R, degStart);
  const p2 = pointOnCircle(R, degEnd);
  const sweep = degEnd - degStart;
  const largeArc = sweep > 180 ? 1 : 0;
  return `M 0 0 L ${p1.x} ${p1.y} A ${R} ${R} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
}

type SegmentGeom = {
  mid: number;
  sweep: number;
  path: string;
  color: string;
  labelY: number;
  foW: number;
  foH: number;
  fontPx: number;
};

function chordWidthAtRadius(radius: number, sweepDeg: number): number {
  const r = Math.abs(radius);
  return 2 * r * Math.sin((sweepDeg * Math.PI) / 360);
}

/** Equal arc per slice for readability; weights only affect `pickWeightedIndex` at spin time. */
function buildSegmentGeometry(
  segments: PrizeSegment[],
  R: number,
  labelY: number,
  compact: boolean
): SegmentGeom[] {
  const n = segments.length;
  if (n === 0) return [];
  const sweep = 360 / n;
  return segments.map((seg, i) => {
    const start = i * sweep;
    const end = start + sweep;
    const mid = start + sweep / 2;
    const chord = chordWidthAtRadius(labelY, sweep);
    const maxW = compact ? Math.min(72, chord * 0.92) : Math.min(132, chord * 0.94);
    const foW = Math.max(compact ? 40 : 48, maxW);
    const foH = compact ? 36 : 52;
    const len = Math.max(4, seg.label.trim().length);
    const fontPx = Math.max(
      compact ? 7 : 8,
      Math.min(compact ? 10 : 12, foW / (len * 0.45))
    );
    return {
      mid,
      sweep,
      path: wedgePath(R, start, end),
      color: PRIZE_WHEEL_SEGMENT_COLORS[i % PRIZE_WHEEL_SEGMENT_COLORS.length],
      labelY,
      foW,
      foH,
      fontPx,
    };
  });
}

const PrizeWheel = ({
  segments,
  onSpinComplete,
  disabled,
  confettiOnWin = true,
  compact = false,
  singleUse = true,
}: PrizeWheelProps) => {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);

  const R = compact ? 82 : 148;
  const labelY = compact ? -48 : -88;
  const svgSize = compact ? 220 : 360;
  const viewCenter = svgSize / 2;

  const segmentsKey = useMemo(
    () => segments.map((s) => `${s.label}:${s.weight}`).join('|'),
    [segments]
  );

  useEffect(() => {
    setHasSpun(false);
    setRotation(0);
    setSpinning(false);
  }, [segmentsKey]);

  const geom = useMemo(
    () => buildSegmentGeometry(segments, R, labelY, compact),
    [segments, R, labelY, compact]
  );

  const handleSpin = useCallback(() => {
    if (spinning || disabled || segments.length === 0) return;
    if (singleUse && hasSpun) return;

    setSpinning(true);
    const winIndex = pickWeightedIndex(segments);
    const centerAngle = geom[winIndex].mid;
    const currentScreen = normDeg(centerAngle + rotation);
    const deltaToPointer = normDeg(360 - currentScreen);
    const fullSpins = 5;
    const targetAngle = 360 * fullSpins + deltaToPointer;
    const newRotation = rotation + targetAngle;

    setRotation(newRotation);

    setTimeout(() => {
      setSpinning(false);
      const label = segments[winIndex].label;
      if (singleUse) setHasSpun(true);
      onSpinComplete?.(label);

      if (confettiOnWin) {
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { y: 0.6 },
          colors: ['#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
        });
      }
    }, 4000);
  }, [
    segments,
    onSpinComplete,
    rotation,
    spinning,
    disabled,
    confettiOnWin,
    geom,
    singleUse,
    hasSpun,
  ]);

  if (segments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-gray-500">
        <p className="text-lg font-medium">No prizes configured</p>
        <p className="text-sm">Ask your tutor to add prize wheel options.</p>
      </div>
    );
  }

  const wheelBox = compact
    ? 'w-[min(88vw,13.75rem)] h-[min(88vw,13.75rem)]'
    : 'w-[min(92vw,22.5rem)] h-[min(92vw,22.5rem)] sm:w-[min(90vw,24rem)] sm:h-[min(90vw,24rem)]';
  const pointerStyle = compact
    ? { borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: '18px solid #111827' }
    : { borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderTop: '28px solid #111827' };

  const showSpinButton = !(singleUse && hasSpun) && !spinning;

  return (
    <div className={`flex flex-col items-center ${compact ? 'gap-3' : 'gap-6'}`}>
      <div className={`relative ${wheelBox} shrink-0`}>
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
          }}
        >
          <svg
            width={svgSize}
            height={svgSize}
            viewBox={`0 0 ${svgSize} ${svgSize}`}
            className="max-w-full max-h-full drop-shadow-lg"
            aria-hidden
          >
            <title>Prize wheel</title>
            <g transform={`translate(${viewCenter}, ${viewCenter})`}>
              {geom.map((g, i) => (
                <path
                  key={`wedge-${i}`}
                  d={g.path}
                  fill={g.color}
                  stroke="rgba(17,24,39,0.35)"
                  strokeWidth={1.25}
                  strokeLinejoin="round"
                />
              ))}
              {geom.map((g, i) =>
                g.sweep >= 8 ? (
                  <g key={`lbl-${i}`} transform={`rotate(${g.mid})`}>
                    <g transform={`translate(0, ${g.labelY}) rotate(${-g.mid})`}>
                      <foreignObject
                        x={-g.foW / 2}
                        y={-g.foH / 2}
                        width={g.foW}
                        height={g.foH}
                      >
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          className="flex h-full w-full items-center justify-center text-center font-bold text-white px-0.5"
                          style={{
                            fontSize: `${g.fontPx}px`,
                            lineHeight: 1.12,
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 4,
                          }}
                        >
                          {segments[i].label}
                        </div>
                      </foreignObject>
                    </g>
                  </g>
                ) : null
              )}
            </g>
          </svg>
        </div>

        <div
          className={`pointer-events-none absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md border-gray-900 ${
            compact ? 'w-[52px] h-[52px] border-[3px]' : 'w-[76px] h-[76px] border-[3px] sm:w-[84px] sm:h-[84px] sm:border-4'
          }`}
        />

        <div
          className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1 z-[2] drop-shadow-md"
          style={pointerStyle}
          aria-hidden
        />
      </div>

      {spinning && (
        <p className="text-sm font-semibold text-amber-800 animate-pulse" role="status">
          Spinning…
        </p>
      )}

      {showSpinButton && (
        <button
          type="button"
          onClick={handleSpin}
          disabled={disabled}
          className={`bg-amber-500 text-white font-bold rounded-xl shadow-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all touch-manipulation ${
            compact ? 'px-5 py-2 text-sm' : 'px-10 py-3.5 text-base sm:text-lg'
          }`}
        >
          {compact ? 'Test spin' : 'Spin the wheel!'}
        </button>
      )}
    </div>
  );
};

export default PrizeWheel;
